import { KeyboardHelper } from '#pplx/frontend/components/utils/keyboardHelpers.js';
import { EMPTY_PROMPT_HISTORY_SELECTION, detachPromptHistory, getPromptHistorySubmissionEvent, isPromptHistorySelectionActive, navigatePromptHistorySelection, preparePromptHistoryValues, } from '#pplx/frontend/composer-sdk-plugins/prompt-history/promptHistoryMechanics.js';
import { useEffect, useMemo, useRef } from 'react';
const isEmptyInput = (value) => value.trim() === '';
const toPlainText = (value) => value;
export function preparePromptHistory(history) {
    return preparePromptHistoryValues(history, toPlainText);
}
export function usePromptHistoryNavigation({ history, ...inputProps }) {
    const preparedHistory = useMemo(() => preparePromptHistory(history), [history]);
    const historyRef = useRef(preparedHistory);
    const inputPropsRef = useRef(inputProps);
    const stateRef = useRef(EMPTY_PROMPT_HISTORY_SELECTION);
    historyRef.current = preparedHistory;
    inputPropsRef.current = inputProps;
    const navigation = useMemo(() => {
        const reset = () => {
            stateRef.current = EMPTY_PROMPT_HISTORY_SELECTION;
        };
        const emitPromptHistoryEvent = (event) => {
            inputPropsRef.current.trackEvent?.(event);
        };
        return {
            reset,
            notifyContentChanged(newValue) {
                if (isEmptyInput(newValue)) {
                    reset();
                    return;
                }
                const state = stateRef.current;
                const { selectedIndex } = state;
                // Raw input changes compare against the live entry, including after history replacement.
                if (selectedIndex === null || newValue === historyRef.current[selectedIndex]) {
                    return;
                }
                const result = detachPromptHistory(state, historyRef.current.length);
                stateRef.current = result.state;
                if (result.event !== undefined)
                    emitPromptHistoryEvent(result.event);
            },
            notifySubmitted(currentValue) {
                const state = stateRef.current;
                const { selectedIndex } = state;
                const event = getPromptHistorySubmissionEvent(state, historyRef.current.length, selectedIndex !== null && currentValue === historyRef.current[selectedIndex]);
                if (event !== undefined)
                    emitPromptHistoryEvent(event);
            },
            handleKeyDown(event) {
                const direction = KeyboardHelper.isArrowUp(event)
                    ? 'up'
                    : KeyboardHelper.isArrowDown(event)
                        ? 'down'
                        : null;
                if (direction === null)
                    return false;
                const state = stateRef.current;
                const selectedIndex = navigatePromptHistorySelection(direction, historyRef.current.length, state, 
                // Content notifications own detachment and blank resets for the raw input adapter.
                isEmptyInput(inputPropsRef.current.value) || isPromptHistorySelectionActive(state));
                if (selectedIndex === undefined)
                    return false;
                stateRef.current = { selectedIndex, detached: false };
                // Keep consuming a selected raw-text entry even if a shorter history no longer has it.
                inputPropsRef.current.setValue(selectedIndex === null ? '' : (historyRef.current[selectedIndex] ?? ''));
                emitPromptHistoryEvent({
                    action: 'navigate',
                    direction,
                    selectedIndex,
                    historySize: historyRef.current.length,
                });
                // Prevent the browser from moving the caret after we handle the arrow key.
                event.preventDefault();
                return true;
            },
        };
    }, []);
    useEffect(() => {
        navigation.reset();
    }, [navigation, inputProps.resetKey]);
    return navigation;
}
//# sourceMappingURL=usePromptHistoryNavigation.js.map